import { ButtonGradientPrimary, Colors, Icon, PageHeader, Text } from '@amelio/ui-library';

import { BANNER_DESCRIPTION, MEETINGS_TITLE } from './meetingStyles';

import meetingsHeroArrow from '@/assets/illustrations/meetingsHeroArrow.png';
import { ARROW_GAP_PX } from '@/components/Illustrations/arrowPlacement';
import { useAmelioTranslations } from '@/hooks';

/** The hand-drawn arrow, in its PNG's own pixels: the canvas and the apex of the arrowhead, read off
 *  the opaque pixels. The ink starts at `x = 0`, so the leading edge of the image is the tail. */
const ARROW_ARTWORK = { width: 292, height: 192, tip: { x: 256, y: 188 } } as const;

/** How far into the button, from its leading edge, the apex comes down: over its leading icon, so
 *  the arrow reads as landing on the action whatever the length of its label. */
const TIP_INSET_PX = 24;

/** The one length the whole drawing is derived from: what the arrow has to cross, from its tail at
 *  the start of the free space beside the banner to its apex over the button's icon. `100%` is that
 *  free space; the artwork draws the same journey in `tip.x` of its own pixels, so every other
 *  artwork length renders at that same ratio. Deriving both ends from this single span is what keeps
 *  the tail on the banner and the apex on the button at every row width — pinning one end and
 *  sizing from the leftovers moves the other. */
const acrossTheGap = (artworkPx: number) =>
    `calc((100% + ${TIP_INSET_PX}px) * ${artworkPx} / ${ARROW_ARTWORK.tip.x})`;

/** The arrow leaves the banner and lands on the button: its tail sits where the space beside the
 *  banner begins, and the drawing is scaled so its apex reaches the button's leading icon. */
const ARROW_PLACEMENT = {
    left: 0,
    bottom: `calc(100% + ${ARROW_GAP_PX - (ARROW_ARTWORK.height - ARROW_ARTWORK.tip.y)}px)`,
    width: acrossTheGap(ARROW_ARTWORK.width),
};

interface MeetingsHeroProps {
    onPrepareMeeting: () => void;
}

export const MeetingsHero = ({ onPrepareMeeting }: MeetingsHeroProps) => {
    const { t } = useAmelioTranslations();

    return (
        // Grid columns rather than a base class paired with its `md:` variant (see
        // src/responsive-utilities.css). `items-start`: a stretched row would inflate the banner's
        // card to the height of the offset button beside it.
        <div className={'grid grid-cols-1 items-start gap-6 md:grid-cols-[minmax(0,519px)_1fr]'}>
            <PageHeader
                // `PageHeader` already wraps `title` in its own `Heading`; a nested `Heading` here
                // would double up the heading semantics, so this stays a `Text` rendered `as='span'`
                // — the only way to carry the navy colour (a `color` prop, not a class: see
                // `MEETINGS_TITLE`'s own doc) without introducing a second heading element.
                title={
                    <Text
                        as={'span'}
                        color={Colors.NAVY}
                        className={MEETINGS_TITLE}
                    >
                        {t('thematicMeetings.list.title')}
                    </Text>
                }
                subtitle={
                    <Text
                        as={'span'}
                        color={Colors.BASE}
                        className={BANNER_DESCRIPTION}
                    >
                        {t('thematicMeetings.list.description')}
                    </Text>
                }
                iconName={'time-calendar-tick'}
                density={'compact'}
                className={'max-w-[519px]'}
            />

            {/* The grid's second column, the button at its end, well below the banner's own row so
                the arrow has room to travel. The arrow's box takes the space left of the button, as
                tall as the button and level with it. Only from `lg`: below it the row is too narrow
                for the drawing. */}
            <div className={'flex md:mt-[243px] md:justify-end'}>
                <div className={'relative flex-1 max-lg:hidden'}>
                    {/* `max-w-none`: the base `max-width: 100%` would cap the drawing at its box,
                        which it deliberately overhangs. */}
                    <img
                        src={meetingsHeroArrow}
                        alt={''}
                        aria-hidden
                        width={ARROW_ARTWORK.width}
                        height={ARROW_ARTWORK.height}
                        className={'pointer-events-none absolute h-auto max-w-none'}
                        style={ARROW_PLACEMENT}
                        data-testid={'thematic-meetings-hero-arrow'}
                    />
                </div>

                <ButtonGradientPrimary
                    onClick={onPrepareMeeting}
                    size={'md'}
                    leftIcon={
                        <Icon
                            name={'essential-add'}
                            size={'md'}
                            strokeWidth={3}
                            color={Colors.WHITE}
                            ariaHidden
                        />
                    }
                    dataTestId={'prepare-meeting-button'}
                >
                    {t('thematicMeetings.list.prepareMeeting')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
};
